import { useNavigate, useParams } from "react-router-dom";
import {
  TextField,
  Button,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  FormControlLabel,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  useLazyGetModuleQuery,
  useAddRoleMutation,
  useLazyGetRoleByIdQuery,
  useUpdateRoleMutation,
} from "../../services/role";
import { showError, showToast } from "../../constants/toasts";

const AddRole = () => {
  const navigate = useNavigate();

  const [modules, setModules] = useState<
    Array<{ id: number; module_name: string }>
  >([]);
  const [permissions, setPermissions] = useState<
    Array<{
      id: number; // module id
      name: string; // module name
      can_view: boolean;
      can_add_edit: boolean;
      can_be_delete: boolean;
      permissionId?: number; // existing permission id when editing
    }>
  >([]);
  const [roleName, setRoleName] = useState<string>("");
  const [roleNameError, setRoleNameError] = useState<string>("");

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isPrefilled, setIsPrefilled] = useState<boolean>(false);
  const [getModules] = useLazyGetModuleQuery();
  const [RoleById] = useLazyGetRoleByIdQuery();
  const [addRole] = useAddRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const { id } = useParams();

  const getDataByIdList = async () => {
    try {
      setIsLoading(true);
      if (!id) return;
      const numericId = Number(id);
      if (Number.isNaN(numericId)) return;
      const response = await RoleById({ id: numericId }).unwrap();
      if (
        (response as any).status === 200 ||
        (response as any).statusCode === 200
      ) {
        const userData = (response as any).data;
        setUser(userData);
        // Prefill the role name field with the fetched data
        if (userData && userData.role_name) {
          setRoleName(userData.role_name);
        }
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      showError(error?.message || error?.data?.message);
    }
  };

  const fetchModules = async () => {
    setIsLoading(true);
    try {
      const response = await getModules({}).unwrap();
      if (response.status === 200) {
        const modulesData = response.data || [];
        const filteredAndOrderedModules = modulesData
          .filter(
            (module: { id: number; module_name: string }) =>
              module.module_name.toLowerCase() !== "onboard"
          )
          .sort(
            (
              a: { id: number; module_name: string },
              b: { id: number; module_name: string }
            ) => {
              const orderMap: { [key: string]: number } = {
                Dashboard: 1,
                "User Management": 2,
                "Family / Shared Account": 3,
                "AI Behavior & Language": 4,
                "Subscription & Plan": 5,
                "Analytics & Insights": 6,
                "Budget Intelligence": 7,
                "Document & OCR Processing": 8,
                "Health & Habit Configuration": 9,
                "Shopping & Meal AI": 10,
                "Label Recognition Editor": 11,
                "Location Intelligence": 12,
                "Translation & Messaging": 13,
                "Notification Management": 14,
                "Revenue Management": 15,
                "Manage Customer Support": 16,
                "Manage CMS Pages": 17,
                Settings: 18,
              };
              const orderA = orderMap[a.module_name] || 999;
              const orderB = orderMap[b.module_name] || 999;
              return orderA - orderB;
            }
          );
        setModules(filteredAndOrderedModules);
        const updatedPermissions = filteredAndOrderedModules.map(
          (module: { id: number; module_name: string }) => ({
            id: module.id,
            name: module.module_name,
            can_view: false,
            can_add_edit: false,
            can_be_delete: false,
            permissionId: undefined,
          })
        );
        setPermissions(updatedPermissions);
      }
    } catch (error) {
      console.log("Error fetching modules:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePermissionChange = (id: number, field: string) => {
    setPermissions((prev) => {
      const updated = prev.map((row) => {
        if (row.id === id) {
          const newValue = !row[field as keyof typeof row];
        
          if (newValue && (field === 'can_add_edit' || field === 'can_be_delete')) {
            return { ...row, [field]: true, can_view: true };
          }
          return { ...row, [field]: newValue };
        }
        return row;
      });

      return updated;
    });
  };

  const handleSubmit = async () => {
    setRoleNameError("");
    if (!roleName.trim()) {
      setRoleNameError("Role name is required");
      return;
    }

    const selectedPermissions = permissions.filter(
      (permission) =>
        permission.can_view ||
        permission.can_add_edit ||
        permission.can_be_delete
    );

    if (selectedPermissions.length === 0) {
      showError("Please select at least one permission");
      return;
    }

    setIsSubmitting(true);
    try {
      const rolePermission = selectedPermissions
        .map((permission) => {
          if (id) {
            if (!permission.id) return null;
            return {
              id:
                user?.permissions
                  ?.map((p: any) => p.id)
                  .find((pid: number) => pid === permission.permissionId) ||
                undefined,
              module: permission.id,
              can_view: permission.can_view,
              can_add_edit: permission.can_add_edit,
              can_be_delete: permission.can_be_delete,
            } as any;
          }
          // Add: send module with flags
          return {
            module: permission.id,
            can_view: permission.can_view,
            can_add_edit: permission.can_add_edit,
            can_be_delete: permission.can_be_delete,
          } as any;
        })
        .filter(Boolean);
      const requestBody: any = {
        role_name: roleName.trim(),
        role_permission: rolePermission,
      };
      if (id) {
        requestBody.id = Number(id);
      }

      const response = id
        ? await updateRole({ body: requestBody }).unwrap()
        : await addRole({ body: requestBody }).unwrap();
      if (response.status === 200 || response.status === 201) {
        showToast("Role created successfully!");
        // Clear form
        setRoleName("");
        setPermissions((prev) =>
          prev.map((p) => ({
            ...p,
            can_view: false,
            can_add_edit: false,
            can_be_delete: false,
          }))
        );
        // Navigate back to roles list
        navigate("/manage-subadmin/roles");
      } else {
        alert("Failed to create role. Please try again.");
      }
    } catch (error: any) {
      console.error("Error creating role:", error);
      alert(error?.data?.message || "Failed to create role. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (id) {
      getDataByIdList();
    }
  }, [id]);
  useEffect(() => {
    fetchModules();
  }, []);

  // Prefill permissions when editing: once user data and modules (permissions state) are available
  useEffect(() => {
    if (!id) return;
    if (!user || !Array.isArray(user.permissions)) return;
    if (!permissions || permissions.length === 0) return;
    if (isPrefilled) return;

    const serverPermissions = user.permissions as Array<{
      id?: number;
      module: string;
      can_view: boolean;
      can_add_edit: boolean;
      can_be_delete: boolean;
    }>;
    const merged = permissions.map((p) => {
      const match = serverPermissions.find(
        (sp) => (sp.module || "").toLowerCase() === (p.name || "").toLowerCase()
      );
      if (!match) return p;
      return {
        ...p,
        can_view: Boolean(match.can_view),
        can_add_edit: Boolean(match.can_add_edit),
        can_be_delete: Boolean(match.can_be_delete),
        permissionId: match.id,
      };
    });

    setPermissions(merged);
    setIsPrefilled(true);
  }, [id, user, permissions, isPrefilled]);
  return (
    <>
      <div className="main_title">
        <h1>{id ? "Edit Role" : "Add Role"}</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate("/manage-subadmin")}>
            Manage Sub-Admin
          </span>{" "}
          -{" "}
          <span onClick={() => navigate("/manage-subadmin/roles")}>
            Manage Roles
          </span>{" "}
          - {id ? "Edit Role" : "Add Role"}
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Role Name</label>
              <TextField
                placeholder="Enter Role Name"
                hiddenLabel
                fullWidth
                value={roleName}
                onChange={(e) => {
                  const valueWithOnlyLetters = e.target.value.replace(/[^a-zA-Z]/g, '');
                  setRoleName(valueWithOnlyLetters);
                  if (roleNameError) setRoleNameError("");
                }}
                onBlur={() => {
                  if (!roleName.trim()) {
                    setRoleNameError("Role name is required");
                  }
                }}
                error={!!roleNameError}
              />
              {roleNameError && (
                <div
                  style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                >
                  {roleNameError}
                </div>
              )}
            </div>
          </div>

          <TableContainer className="table_container mt_20">
            {isLoading ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                Loading modules...
              </div>
            ) : (
              <Table>
                <TableHead>
                  <TableRow hover>
                    <TableCell>Module Name</TableCell>
                    <TableCell align="center">View</TableCell>
                    <TableCell align="center">Add/Edit</TableCell>
                    <TableCell align="center">Delete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {permissions.length > 0 ? (
                    permissions.map((row) => (
                      <TableRow hover key={row.id}>
                        <TableCell>
                          <strong>{row.name}</strong>
                        </TableCell>
                        {[
                          { key: "can_view" },
                          { key: "can_add_edit" },
                          { key: "can_be_delete" },
                        ].map(({ key }) => (
                          <TableCell align="center" key={key}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={Boolean(
                                    row[key as keyof typeof row]
                                  )}
                                  onChange={() =>
                                    handlePermissionChange(row.id, key)
                                  }
                                />
                              }
                              name="check"
                              label=""
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No modules found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          <div className="form_btn">
            <Button
              className="btn btn_primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? id
                  ? "Updating..."
                  : "Creating..."
                : id
                ? "Update"
                : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddRole;
